import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, CalendarDays, RotateCcw, Pause, Play, AlertCircle } from 'lucide-react';
import type { RecurringRule, RecurringFrequency } from '../../types';
import { WeeklyDaySelector, DAYS_OF_WEEK } from '../common';

interface RecurringRuleFormProps {
  value?: RecurringRule;
  onChange: (rule: RecurringRule | undefined) => void;
  showPauseControls?: boolean;
  onPause?: () => void;
  onResume?: () => void;
  required?: boolean; // If true, recurrence cannot be disabled
}

export const RecurringRuleForm: React.FC<RecurringRuleFormProps> = ({
  value,
  onChange,
  showPauseControls = false,
  onPause,
  onResume,
  required = false,
}) => {
  const { t } = useTranslation();
  const defaultRule: RecurringRule = {
    frequency: 'DAILY',
    interval: 1,
  };

  const rule = value || defaultRule;

  const handleEnableRecurring = () => {
    onChange(defaultRule);
  };

  const handleDisableRecurring = () => {
    onChange(undefined);
  };

  const updateRule = (updates: Partial<RecurringRule>) => {
    onChange({ ...rule, ...updates });
  };

  const toggleDayOfWeek = (days: number[]) => {
    updateRule({ daysOfWeek: days });
  };

  const getFrequencyIcon = () => {
    switch (rule.frequency) {
      case 'DAILY':
        return <Calendar className="w-4 h-4" />;
      case 'WEEKLY':
        return <RotateCcw className="w-4 h-4" />;
      case 'MONTHLY':
        return <CalendarDays className="w-4 h-4" />;
    }
  };

  const getFrequencyLabel = () => {
    if (!value) return t('tasks.recurring.noRecurrence');

    let label = '';
    switch (rule.frequency) {
      case 'DAILY':
        label = rule.interval === 1
          ? t('tasks.recurring.dailySingular')
          : t('tasks.recurring.daily', { interval: rule.interval });
        break;
      case 'WEEKLY':
        if (rule.daysOfWeek && rule.daysOfWeek.length > 0) {
          const dayNames = rule.daysOfWeek.map(d => t(DAYS_OF_WEEK[d].labelKey)).join(', ');
          label = rule.interval === 1
            ? t('tasks.recurring.weekly', { days: dayNames })
            : t('tasks.recurring.weeklyWithInterval', { interval: rule.interval, days: dayNames });
        } else {
          label = rule.interval === 1
            ? t('tasks.recurring.weeklyIntervalSingular')
            : t('tasks.recurring.weeklyInterval', { interval: rule.interval });
        }
        break;
      case 'MONTHLY':
        label = t('tasks.recurring.monthly', { day: rule.dayOfMonth || 1 });
        break;
    }

    if (rule.isPaused) {
      label += ' ' + t('tasks.recurring.paused');
    }

    return label;
  };

  // Display mode for paused/resumed status
  if (showPauseControls && value) {
    return (
      <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center gap-2 text-sm">
          {getFrequencyIcon()}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {getFrequencyLabel()}
          </span>
        </div>
        
        {value.isPaused ? (
          <button
            type="button"
            onClick={onResume}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50 transition-colors"
          >
            <Play className="w-3 h-3" />
            {t('tasks.recurring.resume')}
          </button>
        ) : (
          <button
            type="button"
            onClick={onPause}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50 transition-colors"
          >
            <Pause className="w-3 h-3" />
            {t('tasks.recurring.pause')}
          </button>
        )}
        
        <button
          type="button"
          onClick={handleDisableRecurring}
          className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
        >
          {t('tasks.recurring.remove')}
        </button>
      </div>
    );
  }

  // Simple toggle to enable recurring (hidden when required=true)
  if (!value) {
    return (
      <button
        type="button"
        onClick={handleEnableRecurring}
        className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:border-primary-400 dark:hover:text-primary-400 transition-colors"
      >
        <RotateCcw className="w-4 h-4" />
        {t('tasks.recurring.addRecurrence')}
        </button>
    );
  }

  return (
    <div className="space-y-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          {getFrequencyIcon()}
          {t('tasks.recurring.recurringTask')}
        </div>
        {!required && (
          <button
            type="button"
            onClick={handleDisableRecurring}
            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
          >
            {t('tasks.recurring.remove')}
          </button>
        )}
      </div>

      {/* Frequency Selection */}
      <div className="grid grid-cols-3 gap-1 sm:gap-2">
        {(['DAILY', 'WEEKLY', 'MONTHLY'] as RecurringFrequency[]).map((freq) => (
          <button
            key={freq}
            type="button"
            onClick={() => updateRule({ 
              frequency: freq,
              // Set defaults for each frequency
              ...(freq === 'WEEKLY' && !rule.daysOfWeek ? { daysOfWeek: [1] } : {}),
              ...(freq === 'MONTHLY' && !rule.dayOfMonth ? { dayOfMonth: 1 } : {}),
            })}
            className={`
              px-3 py-2 text-sm rounded-lg border transition-colors
              ${rule.frequency === freq
                ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-400'
              }
            `}
          >
            {t('tasks.recurring.frequency.' + freq.toLowerCase())}
          </button>
        ))}
      </div>

      {/* Interval */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400">{t('tasks.recurring.every')}</label>
        <input
          type="number"
          min={1}
          max={99}
          value={rule.interval}
          onChange={(e) => updateRule({ interval: parseInt(e.target.value) || 1 })}
          className="w-20 sm:w-16 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {rule.frequency === 'DAILY' && (rule.interval === 1 ? t('tasks.recurring.day') : t('tasks.recurring.days'))}
          {rule.frequency === 'WEEKLY' && (rule.interval === 1 ? t('tasks.recurring.week') : t('tasks.recurring.weeks'))}
          {rule.frequency === 'MONTHLY' && (rule.interval === 1 ? t('tasks.recurring.month') : t('tasks.recurring.months'))}
        </span>
      </div>

      {/* Days of Week (for WEEKLY) */}
      {rule.frequency === 'WEEKLY' && (
        <div>
          <WeeklyDaySelector
            value={rule.daysOfWeek || []}
            onChange={toggleDayOfWeek}
            label={t('tasks.recurring.onDays')}
            size="sm"
            showIcon={false}
          />
          {(!rule.daysOfWeek || rule.daysOfWeek.length === 0) && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {t('tasks.recurring.selectAtLeastOneDay')}
            </p>
          )}
        </div>
      )}

      {/* Day of Month (for MONTHLY) */}
      {rule.frequency === 'MONTHLY' && (
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-400">{t('tasks.recurring.onDay')}</label>
          <input
            type="number"
            min={1}
            max={31}
            value={rule.dayOfMonth || 1}
            onChange={(e) => updateRule({ dayOfMonth: parseInt(e.target.value) || 1 })}
            className="w-20 sm:w-16 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t('tasks.recurring.adjustedForMonths')}
          </span>
        </div>
      )}

      {/* Time */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {t('tasks.recurring.time')}
        </label>
        <input
          type="time"
          value={rule.time || '09:00'}
          onChange={(e) => updateRule({ time: e.target.value })}
          className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {/* End Conditions */}
      <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <label className="block text-sm text-gray-600 dark:text-gray-400">{t('tasks.recurring.endConditions')}</label>
        
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-500 dark:text-gray-400">{t('tasks.recurring.endDateLabel')}</label>
          <input
            type="date"
            value={rule.endDate ? rule.endDate.split('T')[0] : ''}
            onChange={(e) => updateRule({ endDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
            className="flex-1 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-500 dark:text-gray-400">{t('tasks.recurring.maxOccurrencesLabel')}</label>
          <input
            type="number"
            min={1}
            max={999}
            value={rule.maxOccurrences || ''}
            onChange={(e) => updateRule({ maxOccurrences: e.target.value ? parseInt(e.target.value) : undefined })}
            placeholder={t('tasks.recurring.unlimited')}
            className="w-24 px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="p-2 rounded bg-primary-50 dark:bg-primary-900/20 text-xs text-primary-700 dark:text-primary-300">
        <strong>{t('tasks.recurring.summary')}</strong> {getFrequencyLabel()}
        {rule.endDate && ` ${t('tasks.recurring.until', { date: new Date(rule.endDate).toLocaleDateString() })}`}
        {rule.maxOccurrences && ` ${t('tasks.recurring.forOccurrences', { count: rule.maxOccurrences })}`}
      </div>
    </div>
  );
};

export default RecurringRuleForm;